/* eslint-disable react-doctor/prefer-tag-over-role -- <dialog> element has conflicting browser defaults (position, margin) that would break our portal-based positioning */
'use client';

import React, { useEffect, useRef } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { formatMrr } from '@/utils/formatters';
import { track } from '@/utils/analytics';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { Startup } from '@/types/data';

interface ClusterEntry {
  name: string;
  count: number;
}

interface Props {
  startup: Startup;
  onClose: () => void;
  clusterEntry?: ClusterEntry | null;
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const getCountryName = (code: string): string => {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
};

const normalizeWebsiteUrl = (url: string): string | null => {
  try {
    const href = url.startsWith('http') ? url : `https://${url}`;
    const parsed = new URL(href);
    return parsed.hostname.includes('.') ? href : null;
  } catch {
    return null;
  }
};

export const StartupCard: React.FC<Props> = ({ startup, onClose, clusterEntry }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(cardRef);

  useEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();
    return () => { prevFocus?.focus(); };
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleView = (): void => {
    track({ type: 'startup_clicked', slug: startup.slug, name: startup.name });
    window.open(`https://trustmrr.com/startup/${startup.slug}`, '_blank');
  };

  const growthSign = startup.growth_mrr_30d != null && startup.growth_mrr_30d > 0 ? '+' : '';
  const growthColor =
    startup.growth_mrr_30d == null
      ? ''
      : startup.growth_mrr_30d > 0
        ? 'text-accent'
        : 'text-text-secondary';

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Details for ${startup.name}`}
      style={{ backgroundColor: 'var(--bg-panel)' }}
      className="fixed z-[200] border border-border rounded-sm shadow-lg overflow-y-auto
                 bottom-[68px] left-3 right-3 max-h-[calc(100dvh-136px)]
                 desk:bottom-4 desk:left-1/2 desk:-translate-x-1/2 desk:right-auto desk:w-[300px] desk:max-h-[calc(100dvh-80px)]"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-[10px] border-b border-border">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-text-primary leading-snug truncate">
            {startup.name}
          </div>
          <div className="text-[10px] text-text-muted mt-[3px] leading-snug truncate">
            {startup.category ?? ''}{startup.subcategory ? ` · ${startup.subcategory}` : ''}
          </div>
        </div>
        <button
          ref={closeBtnRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-[6px] -mr-[6px] text-text-muted hover:text-text-secondary transition-colors flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>

      {/* Stats grid */}
      <div className="px-3 py-[10px] flex flex-wrap gap-x-5 gap-y-[8px]">
        <div>
          <div className="text-[9px] font-semibold tracking-[0.1em] uppercase text-text-muted">MRR</div>
          <div className="text-[12px] font-medium text-text-secondary mt-[1px]">{formatMrr(startup.mrr)} USD</div>
        </div>

        {startup.customers != null && startup.customers > 0 && (
          <div>
            <div className="text-[9px] font-semibold tracking-[0.1em] uppercase text-text-muted">Customers</div>
            <div className="text-[12px] font-medium text-text-secondary mt-[1px]">
              {startup.customers.toLocaleString()}
            </div>
          </div>
        )}

        {startup.growth_mrr_30d != null && (
          <div>
            <div className="text-[9px] font-semibold tracking-[0.1em] uppercase text-text-muted">30d Growth</div>
            <div className={`text-[12px] font-medium mt-[1px] ${growthColor}`}>
              {growthSign}{startup.growth_mrr_30d.toFixed(1)}%
            </div>
          </div>
        )}

        {startup.country && (
          <div>
            <div className="text-[9px] font-semibold tracking-[0.1em] uppercase text-text-muted">Country</div>
            <div className="text-[12px] font-medium text-text-secondary mt-[1px]">
              {getCountryName(startup.country)}
            </div>
          </div>
        )}
      </div>

      {/* Badges */}
      {(startup.target_audience || startup.on_sale) && (
        <div className="px-3 pb-[10px] flex flex-wrap gap-[4px]">
          {startup.target_audience && (
            <span className="text-[9px] font-medium px-[6px] py-[2px] rounded-sm border border-border text-text-muted tracking-[0.04em]">
              {startup.target_audience}
            </span>
          )}
          {startup.on_sale && (
            <span className="text-[9px] font-medium px-[6px] py-[2px] rounded-sm border border-accent text-accent bg-accent-dim tracking-[0.04em]">
              For sale
            </span>
          )}
        </div>
      )}


      {/* Cluster */}
      {clusterEntry && clusterEntry.name !== 'Uncategorized' && (
        <div className="px-3 pb-[10px] border-t border-border pt-[10px]">
          <div className="text-[9px] font-semibold tracking-[0.1em] uppercase text-text-muted">Cluster</div>
          <div className="text-[11px] font-medium text-text-secondary mt-[2px] leading-snug">{clusterEntry.name}</div>
          <div className="text-[10px] text-text-muted mt-[1px]">{clusterEntry.count.toLocaleString()} similar startups</div>
        </div>
      )}

      {/* CTA */}
      <div className="px-3 pb-3 flex flex-col gap-[6px]">
        {startup.website && (() => {
          const href = normalizeWebsiteUrl(startup.website);
          return href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track({ type: 'startup_clicked', slug: startup.slug, name: startup.name })}
              className="w-full text-[11px] font-medium py-[7px] rounded-sm border border-accent text-accent transition-colors hover:bg-accent-bright tracking-[0.04em] flex items-center justify-center gap-[6px]"
            >
              Visit website <ExternalLink size={11} />
            </a>
          ) : null;
        })()}
        <button
          type="button"
          onClick={handleView}
          className="w-full text-[11px] font-medium py-[7px] rounded-sm border border-border text-text-secondary cursor-pointer transition-colors hover:border-accent hover:text-accent tracking-[0.04em]"
        >
          <span className="flex items-center justify-center gap-[6px]">
            View on TrustMRR <ExternalLink size={11} />
          </span>
        </button>
      </div>
    </div>
  );
};
